<template>
    <card>
        <template #header>
            <div class="d-flex align-items-center justify-content-between">
                <h1 class="card-title mb-0">Failed Faxes</h1>
                <div class="d-flex align-items-center" style="gap: 10px;">
                    <DateRangeFilter v-model="dateRange" :clearable="false" @input="onDateChange" style="max-width: 400px;" />
                    <b-form-radio-group v-model="selectedView" :options="viewOptions"
                        button-variant="primary" buttons />
                </div>
            </div>
        </template>
        <b-alert :show="!!dateRangeWarning" variant="warning" dismissible @dismissed="dateRangeWarning = ''">
            {{ dateRangeWarning }}
        </b-alert>
        <b-row>
            <b-col>
                <b-table id="failed-fax-table" ref="failed-fax-table" :api-url="apiUrl" outlined show-empty
                    no-provider-sorting no-provider-filtering :filter-included-fields="['messageId']" :filter="hiddenIds"
                    :filter-function="filterFunction" :fields="faxFields" :items="faxTableProvider">
                    <template #table-busy>
                        <div class="text-center my-2">
                            <b-spinner class="align-middle mr-2"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>
                    <template #cell(actions)="row">
                        <div class="d-flex" style="gap:15px;" v-if="!isArchivedTab">
                            <b-button variant="outline-info" size="sm" @click="changeNumber(row)" v-b-tooltip.hover
                                title="Change Fax Number">
                                <b-icon-pencil-square />
                            </b-button>
                            <b-button variant="outline-primary" size="sm" @click="resend(row)" v-b-tooltip.hover
                                title="Resend">
                                <b-icon-arrow-repeat />
                            </b-button>
                            <b-button variant="outline-danger" size="sm" @click="archive(row)" v-b-tooltip.hover
                                title="Archive">
                                <b-icon-archive />
                            </b-button>
                        </div>
                        <div class="d-flex" style="gap:15px;" v-else>
                            <b-button variant="outline-success" size="sm" @click="unarchive(row)" v-b-tooltip.hover
                                title="Unarchive">
                                <b-icon-arrow-counterclockwise />
                            </b-button>
                        </div>
                    </template>
                    <template #cell(details)="row">
                        <i :class="detailsClass(row)" role="button" @click="selectRow(row)" />
                    </template>
                    <template #row-details="row">
                        <b-card>
                            <div :id="`pdf-viewer-${row.item.messageId}`" v-b-visible="imageSource(row)" />
                        </b-card>
                    </template>
                </b-table>
            </b-col>
        </b-row>
        <BottomPagerBar hide-pager hide-right :loading="loading" no-stick>
            <template #left>
                <b-button @click="reloadFaxes" variant="outline-primary" size="sm" class="m-0">
                    <b-icon-arrow-clockwise /> Refresh
                </b-button>
            </template>
            <template v-if="isArchivedTab" #middle>
                <div class="d-flex align-items-center justify-content-center" style="gap: 10px;">
                    <b-button variant="primary" size="sm" class="m-0" @click="loadPrevious" :disabled="pageNbr <= 0">
                        <b-icon-chevron-left />
                    </b-button>
                    <span class="text-muted">Page {{ pageNbr + 1 }}</span>
                    <b-button variant="primary" size="sm" class="m-0" @click="loadNext" :disabled="currentPageCount < pageSize">
                        <b-icon-chevron-right />
                    </b-button>
                </div>
            </template>
        </BottomPagerBar>
        <b-modal id="fax-number-change-modal" size="sm" title="Fax Number">
            <b-input v-model="selectedFax.faxNumber" />
        </b-modal>
    </card>
</template>
<script lang="ts">
import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { BTable, BvTableCtxObject, BvTableFieldArray } from "bootstrap-vue";
import moment from "moment/moment";
import { debounce } from "throttle-debounce";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import PDFObject from "pdfobject";
import TextUtils from "@/util/TextUtils";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import DateRangeFilter from "@/components/DateRangeFilter.vue";

@Component({
    name: "FailedFaxComponent",
    components: { BottomPagerBar, DateRangeFilter },
})
export default class FailedFaxComponent extends Vue {
    @Ref("failed-fax-table") faxTable!: BTable;
    hiddenIds: string[] = [];
    selectedView: string = "Active";
    pageNbr: number = 0;
    pageSize: number = 50;
    currentPageCount: number = 0;
    dateRange: { startDate: any, endDate: any } = {
        startDate: moment().subtract(30, "days").toDate(),
        endDate: new Date(),
    };
    dateRangeWarning: string = "";
    loading: boolean = false;

    private hasCustomFields: boolean = false;
    private readonly maxDays: number = 90;
    private abortController: AbortController | null = null;

    get viewOptions() {
        return [
            { text: "Active", value: "Active" },
            { text: "Archived", value: "Archived" },
        ];
    }

    get isArchivedTab(): boolean {
        return this.selectedView === "Archived";
    }

    get apiUrl(): string {
        const fromDate = moment(this.dateRange.startDate).format("YYYY-MM-DD");
        const toDate = moment(this.dateRange.endDate).format("YYYY-MM-DD");
        const dateParams = `&fromDate=${fromDate}&toDate=${toDate}`;
        if (this.isArchivedTab) {
            const offset = this.pageNbr * this.pageSize;
            return `/api/Fax/failed?archived=true&offset=${offset}&limit=${this.pageSize}${dateParams}`;
        }
        return `/api/Fax/failed?${dateParams}`;
    }

    get faxFields(): BvTableFieldArray {
        return [
            this.hasCustomFields ? {
                key: "cf.faxDocumentType",
                label: "Fax Type",
                sortable: true,
            } : "",
            { key: "messageId" },
            {
                key: "faxNumber",
                sortable: true,
            },
            { key: "resultInfo" },
            { key: "pagesCount" },
            {
                key: "errorCode",
                sortable: true,
            },
            { key: "resultCode" },
            {
                key: "createdAt",
                formatter: (d: any) => moment(d).format("L LT"),
                sortable: true,
            },
            {
                key: "resolvedDate",
                formatter: (d: any) => moment(d).format("L LT"),
                sortable: true,
            },
            {
                key: "actions",
                class: "text-center",
            },
            {
                key: "details",
                class: "text-center",
            },

        ];
    }

    detailsClass(r: any) {
        return `text-primary fa fa-eye${r.detailsShowing ? '-slash' : ''}`;
    }

    reloadFaxes() {
        this.hiddenIds = [];
        this.faxTable.refresh();
    }

    @Watch("selectedView")
    onViewChange() {
        this.pageNbr = 0;
        this.$nextTick(() => {
            this.reloadFaxes();
        });
    }

    private debouncedReload = debounce(500, () => {
        this.pageNbr = 0;
        this.$nextTick(() => {
            this.reloadFaxes();
        });
    });

    onDateChange() {
        const from = moment(this.dateRange.startDate);
        const to = moment(this.dateRange.endDate);

        if (from.isValid() && to.isValid()) {
            const daysDiff = to.diff(from, "days");
            if (daysDiff > this.maxDays) {
                this.dateRange = { startDate: this.dateRange.startDate, endDate: from.clone().add(this.maxDays, "days").toDate() };
                this.dateRangeWarning = `Date range cannot exceed ${this.maxDays} days. The "To" date has been adjusted.`;
                this.debouncedReload();
                return;
            } else if (daysDiff < 0) {
                this.dateRange = { startDate: this.dateRange.startDate, endDate: this.dateRange.startDate };
                this.dateRangeWarning = `"To" date cannot be before "From" date. It has been adjusted.`;
                this.debouncedReload();
                return;
            }
        }

        this.dateRangeWarning = "";
        this.debouncedReload();
    }

    loadPrevious() {
        this.pageNbr--;
        this.$nextTick(() => {
            this.reloadFaxes();
        });
    }

    loadNext() {
        this.pageNbr++;
        this.$nextTick(() => {
            this.reloadFaxes();
        });
    }

    filterFunction(item: any, filt: any[]): boolean {
        const messageId = item.messageId;
        return !filt.find(v => v == messageId);
    }

    selectedFax: any = {};

    async changeNumber(row: any) {
        this.selectedFax = row.item;
        this.$bvModal.show("fax-number-change-modal");
    }

    async resend(row: any) {
        const messageId = row.item.messageId;
        this.hiddenIds.push(messageId);
        try {
            const faxNumber = TextUtils.formatFaxNumber(row.item.faxNumber);
            await axios.post(`/api/Fax/resend/${row.item.messageId}`, { faxNumber: faxNumber });
            this.$notification(NotificationOptions.notificationOptionsPreset("Successfully re-sent",
                NotificationOptions.NotificationTypes.success));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            this.hiddenIds = this.hiddenIds.filter(id => id !== messageId);
        }
    }

    async archive(row: any) {
        const messageId = row.item.messageId;
        this.hiddenIds.push(messageId);
        try {
            await axios.post(`/api/Fax/archive/${messageId}`);
            this.$notification(NotificationOptions.notificationOptionsPreset("Successfully archived",
                NotificationOptions.NotificationTypes.success));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            this.hiddenIds = this.hiddenIds.filter(id => id !== messageId);
        }
    }

    async unarchive(row: any) {
        const messageId = row.item.messageId;
        this.hiddenIds.push(messageId);
        try {
            await axios.post(`/api/Fax/unarchive/${messageId}`);
            this.$notification(NotificationOptions.notificationOptionsPreset("Successfully unarchived",
                NotificationOptions.NotificationTypes.success));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            this.hiddenIds = this.hiddenIds.filter(id => id !== messageId);
        }
    }

    selectRow(r: any) {
        r.toggleDetails();
    }

    imageSource(row: any) {
        const imageSource = `${axios.defaults.baseURL}/fax/image/${row.item.messageId}`;
        axios.get(imageSource, { responseType: "blob" })
            .then(r => {
                const objectUrl = URL.createObjectURL(r.data);
                const options = {
                    pdfOpenParams: {
                        view: 'FitW',
                        pagemode: 'thumbs',
                        toolbar: 1,
                        scrollbar: 0,
                    },
                };
                PDFObject.embed(objectUrl, `#pdf-viewer-${row.item.messageId}`, options);
            }).catch(err => this.$notification(NotificationOptions.error(err)));

        return imageSource;
    }

    async faxTableProvider(ctx: BvTableCtxObject): Promise<any[]> {
        const uri = ctx.apiUrl + "";
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.loading = true;
        try {
            this.hasCustomFields = false;
            const resp = await axios.get<any[]>(uri, { signal: this.abortController.signal });
            const faxes = resp.data as any[];
            this.currentPageCount = faxes.length;

            if (faxes.some(f => f.cf != undefined && f.cf.faxDocumentType != undefined)) {
                this.hasCustomFields = true;
            }

            return faxes;
        } catch (err) {
            if (axios.isCancel(err)) return [];
            this.$notification(NotificationOptions.error(err));
            this.currentPageCount = 0;
            return [];
        } finally {
            this.loading = false;
        }
    }
};
</script>
